import React, { SFC, useEffect, useState } from "react";
import { Card, Grid, Typography, Button, Box, useTheme, useMediaQuery, PropTypes as MuIPropTypes, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { TextField } from "formik-material-ui";
import { Formik, Form, Field} from "formik";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import {states} from "../../Constants/locations";
import { foundUsOptions } from "../../Constants/foundUsOptions";
import MenuItem from "@material-ui/core/MenuItem";
import moment from "moment-timezone";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../redux/store";
import {createUserProfile, getUserProfile, patchUserProfile} from "../../redux/userProfile/userProfile.actions";

const useStyle = makeStyles(() => ({
    wrapper: {
        padding: "1.5em"
    },
    formItem: {
        marginBottom: "1em",
    },
    inputGroup: {
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center"
    }
}));

interface Props {
	isOnBoarding: boolean,
	setIsOnBoarding: React.Dispatch<React.SetStateAction<boolean>>,
}

const PartnerAdminInfo: SFC<Props> = ({isOnBoarding, setIsOnBoarding}: Props) => {
    const classes = useStyle();
    const theme = useTheme();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const isWhiteLabel = useSelector((state: RootState) => state?.branding.partnerPreferencesOn);
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const textAlignmentAdaptive: MuIPropTypes.Alignment = isMobile ? "center" : "left";
    let initialValues = {
        firstName: "",
        lastName: "",
        phone: "",
        position: ""
    };
    const phoneRegex = new RegExp("^(\\+\\d{1,2}\\s?)?\\(?\\d{3}\\)?[\\s.-]?\\d{3}[\\s.-]?\\d{4}$");
    const [profileId, setProfileId] = useState(0);
    const [formikValues, setFormikValues] = useState(initialValues);
    const [isProfileExists, setIsProfileExists] = useState(true);
    const [isLoaderShowed, setIsLoaderShowed] = useState(false);
    const validationSchema = Yup.object().shape({
        firstName: Yup.string().required("Required"),
        lastName: Yup.string().required("Required"),
        position: Yup.string().required("Required"),
        phone: Yup.string().matches(phoneRegex, "phone must match the following: (xxx)xxx-xxxx"),
    });
    const user = useSelector((state: RootState) => state?.user);
    const dispatch = useAppDispatch();

    useEffect(() => {
        setIsLoaderShowed(true);
        dispatch(getUserProfile({userId: user.userId})).then((profile) => {
            // Presetting fields with data from BE
            if(profile.length){
                setProfileId(profile[0].id);
                const newFormikValues = {...formikValues};
                if(profile[0].firstName){
                    newFormikValues.firstName = profile[0].firstName;
                }
                if(profile[0].lastName){
                    newFormikValues.lastName = profile[0].lastName;
                }
                if(profile[0].phoneNumber) {
                    newFormikValues.phone = profile[0].phoneNumber;
                }
                if(profile[0].position) {
                    newFormikValues.position = profile[0].position;
                }
                setFormikValues({...newFormikValues});
            } else {
                // Changing that, so we could know that we are creating the new profile;
                setIsProfileExists(false);
            }
        }).finally(() => {
            setIsLoaderShowed(false);
        });
    }, []);
    return (
        <Formik
            initialValues={formikValues}
            enableReinitialize
            onSubmit={async (values) => {
                try {
                    const practiceCode = "01";
                    const userId = user.userId;
                    const phoneNumber = values.phone;
                    const type = "owner";
                    let body = {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        position: values.position,
                        practiceCode,
                        userId,
                        type,
                        locationTimeZone: moment.tz.guess()
                    };
                    if(phoneNumber) Object.assign(body, {phoneNumber});
                    if(isProfileExists){
                        dispatch(patchUserProfile({id: profileId, body: body}));
                    } else {
                        dispatch(createUserProfile(body));
                    }
                    if(isOnBoarding){
                        enqueueSnackbar("Profile is saved. You can now specify branding preferences", {
                            autoHideDuration: 2500
                        });
                        setIsOnBoarding(false);
                        document.cookie = "isOnBoarding= ; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
                        history.push("/preferences");
                    } else {
                        enqueueSnackbar("Profile is saved.", {
                            autoHideDuration: 2500
                        });
                    }
                } catch (error) {
                    enqueueSnackbar("Profile for user already exists.", {
                        variant: "error",
                        autoHideDuration: 3000,
                    });

                }
            }}
            validationSchema={validationSchema}
        >
            {(props) => {
                return (
                    <Form>
                        <Card variant="outlined">
                            {/* Desiding if we want to show form or loader */}
                            {
                                !isLoaderShowed ?
                                    <Grid container className={classes.wrapper}>
                                        <Grid item container justify="space-between" alignItems="center">
                                            <Grid container alignItems="center" item xs={12}>
                                                <Box marginBottom="10px">
                                                    <Typography variant="h4">Basic Info</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item container alignItems="center" className={classes.formItem}>
                                                <Grid item xs={12} sm={6}>
                                                    <Box marginBottom="10px">
                                                        <Typography variant="h5" align={textAlignmentAdaptive}>Name</Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Field
                                                        component={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        label="Your first name"
                                                        name="firstName"
                                                        type="text"
                                                        onChange={props.handleChange("firstName")}
                                                        onBlur={props.handleBlur("firstName")}
                                                        value={props.values.firstName}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid item container alignItems="center" className={classes.formItem}>
                                                <Grid item xs={12} sm={6}>
                                                    <Box marginBottom="10px">
                                                        <Typography variant="h5" align={textAlignmentAdaptive}>Last Name</Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Field
                                                        component={TextField}
                                                        variant="outlined"
                                                        fullWidth
                                                        label="Your last name"
                                                        name="lastName"
                                                        type="text"
                                                        onChange={props.handleChange("lastName")}
                                                        onBlur={props.handleBlur("lastName")}
                                                        value={props.values.lastName}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid container justify="space-between" alignItems="center" className={classes.formItem}>
                                            <Grid item xs={12} sm={6}>
                                                <Box marginBottom="10px">
                                                    <Typography variant="h5" align={textAlignmentAdaptive}>Phone</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field
                                                    component={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    label="Your phone"
                                                    name="phone"
                                                    type="tel"
                                                    onChange={props.handleChange("phone")}
                                                    onBlur={props.handleBlur("phone")}
                                                    value={props.values.phone}
                                                />
                                            </Grid>
                                        </Grid>
                                        { user.role === "PARTNER" &&
											<Grid item container alignItems="center" className={classes.formItem}>
											    <Grid item xs={12} sm={6}>
											        <Box marginBottom="10px">
											            <Typography variant="h5" align={textAlignmentAdaptive}>Position</Typography>
											        </Box>
											    </Grid>
											    <Grid item xs={12} sm={6}>
											        <Field
											            component={TextField}
											            variant="outlined"
											            fullWidth
											            label="Your position"
											            name="position"
											            type="text"
											            onChange={props.handleChange("position")}
											            onBlur={props.handleBlur("position")}
											            value={props.values.position}
											        />
											    </Grid>
											</Grid>
                                        }
                                        <Grid item xs={12}>
                                            <Grid container justify="flex-end">
                                                <Button variant="contained" fullWidth={isMobile} color="secondary" type="submit">Save</Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    :
                                    <Box height="400px" width="100%" display="flex" justifyContent="center" alignItems="center">
                                        <CircularProgress/>
                                    </Box>
                            }
                        </Card>
                    </Form>
                );
            }}
        </Formik>

    );
};

export default PartnerAdminInfo;
